/* ZİKRA — global theme scripts (no dependencies) */
(() => {
  const Z = (window.ZIKRA = window.ZIKRA || {});
  const strings = Z.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------------- */
  /* Utilities                                                              */
  /* ---------------------------------------------------------------------- */

  Z.formatMoney = (cents, format = Z.moneyFormat || '{{amount_with_comma_separator}} TL') => {
    const value = typeof cents === 'string' ? Number(cents.replace(/[^\d]/g, '')) : Number(cents);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(amount) || amount == null) return '0';
      const fixed = (amount / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const match = format.match(placeholder);
    if (!match) return format;
    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals': formatted = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator': formatted = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': formatted = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': formatted = withDelimiters(value, 2, "'", '.'); break;
      case 'amount_with_space_separator': formatted = withDelimiters(value, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': formatted = withDelimiters(value, 2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': formatted = withDelimiters(value, 0, ' ', ''); break;
      default: formatted = withDelimiters(value, 2);
    }
    return format.replace(placeholder, formatted);
  };

  Z.debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  Z.announce = (message) => {
    const region = document.getElementById('A11yAnnouncer');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };

  /* Consent-aware: Shopify routes custom events to web pixels according to visitor consent. */
  Z.publish = (name, detail = {}) => {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, detail);
      }
    } catch (error) { /* analytics must never break the storefront */ }
    document.dispatchEvent(new CustomEvent(`zikra:${name}`, { detail }));
  };

  let lockCount = 0;
  Z.lockScroll = () => { lockCount += 1; document.body.classList.add('is-locked'); };
  Z.unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.classList.remove('is-locked');
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------------------------------------------------------------------- */
  /* Cart API (Shopify AJAX API + Section Rendering API)                    */
  /* ---------------------------------------------------------------------- */

  const Cart = {
    sectionIds() {
      const ids = ['cart-drawer'];
      const page = document.querySelector('cart-page');
      if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
      return ids;
    },

    async request(url, options) {
      let response;
      try {
        response = await fetch(url, options);
      } catch (networkError) {
        const error = new Error(strings.errorNetwork);
        error.description = strings.errorNetwork;
        throw error;
      }
      let data = {};
      try { data = await response.json(); } catch (parseError) { data = {}; }
      if (!response.ok) {
        const error = new Error(data.description || data.message || strings.errorGeneric);
        error.description = data.description || data.message || strings.errorGeneric;
        error.status = response.status;
        throw error;
      }
      return data;
    },

    add(formData, { render = true } = {}) {
      if (render) {
        formData.append('sections', this.sectionIds().join(','));
        formData.append('sections_url', window.location.pathname);
      }
      return this.request(`${Z.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
    },

    /* Sets a product row's total quantity across all of its (discount-split) line keys. */
    updateKeys(keys, quantity) {
      const updates = {};
      keys.forEach((key, index) => { updates[key] = index === 0 ? quantity : 0; });
      return this.request(`${Z.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
    },

    change(line, quantity) {
      return this.request(`${Z.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line,
          quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname
        })
      });
    },

    updateNote: Z.debounce((note) => {
      fetch(`${Z.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      }).catch(() => {});
    }, 500),

    render(sections) {
      if (!sections) return;
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) drawer.renderFrom(sections['cart-drawer']);
      const page = document.querySelector('cart-page');
      if (page && sections[page.dataset.sectionId]) page.renderFrom(sections[page.dataset.sectionId]);
    },

    setCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        if (el.hasAttribute('data-hide-empty')) el.hidden = count < 1;
      });
      document.querySelectorAll('[data-cart-label]').forEach((link) => {
        link.setAttribute(
          'aria-label',
          count > 0 ? (strings.cartWithCount || '').replace('[n]', count) : strings.cartEmpty || ''
        );
      });
    }
  };
  Z.cart = Cart;

  /* Tracks the quantity-offer state across renders so the "applied" event fires once per change. */
  let lastTierState = null;
  const trackTierState = (root) => {
    const el = root.querySelector('[data-tier-state]');
    const state = el ? Number(el.dataset.tierState) : 0;
    if (lastTierState !== null && state >= 2 && state !== lastTierState) {
      Z.publish('zikra_quantity_discount_applied', { tier: state });
    }
    lastTierState = state;
  };

  /* ---------------------------------------------------------------------- */
  /* Drawer base (native <dialog>: focus trap, Escape, inert background)    */
  /* ---------------------------------------------------------------------- */

  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.dialog.dataset.bound) return;
      this.dialog.dataset.bound = 'true';
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
    }

    get isOpen() { return this.dialog && this.dialog.open; }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      Z.lockScroll();
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
      this.onOpen();
    }

    close() {
      if (!this.dialog || !this.dialog.open || this.closing) return;
      this.closing = true;
      this.dialog.classList.remove('is-open');
      const finish = () => {
        this.dialog.close();
        this.closing = false;
        Z.unlockScroll();
        this.onClose();
        if (this.opener && typeof this.opener.focus === 'function' && document.contains(this.opener)) {
          this.opener.focus({ preventScroll: true });
        }
      };
      if (reduceMotion.matches) finish();
      else window.setTimeout(finish, 320);
    }

    onOpen() {}
    onClose() {}
  }

  class MenuDrawer extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      this.toggle = document.querySelector('[data-menu-open]');
      if (this.toggle && !this.toggle.dataset.bound) {
        this.toggle.dataset.bound = 'true';
        this.toggle.addEventListener('click', () => this.open(this.toggle));
      }
      if (this.dialog && !this.dialog.dataset.linksBound) {
        this.dialog.dataset.linksBound = 'true';
        this.dialog.addEventListener('click', (event) => {
          if (event.target.closest('a[href]')) this.close();
        });
      }
    }
    onOpen() { this.toggle && this.toggle.setAttribute('aria-expanded', 'true'); }
    onClose() { this.toggle && this.toggle.setAttribute('aria-expanded', 'false'); }
  }
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  /* ---------------------------------------------------------------------- */
  /* Cart drawer                                                            */
  /* ---------------------------------------------------------------------- */

  class CartDrawer extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      lastTierState = null;
      trackTierState(this);

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-cart-open]');
        if (!trigger || Z.cartType !== 'drawer') return;
        if (document.body.classList.contains('template-cart')) return;
        event.preventDefault();
        this.open(trigger);
      });

      bindCartLines(this, () => this.querySelector('[data-cart-drawer-body]'));
    }

    onOpen() {
      Z.publish('zikra_cart_drawer_opened', { item_count: Number(this.dataset.itemCount || 0) });
    }

    renderFrom(html) {
      const doc = parseHTML(html);
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;
      const focusKey = getFocusKey(this);
      ['[data-cart-drawer-body]', '[data-cart-drawer-footer]', '[data-cart-drawer-count]'].forEach((selector) => {
        const current = this.querySelector(selector);
        const next = fresh.querySelector(selector);
        if (current && next) current.replaceWith(next);
      });
      this.dataset.itemCount = fresh.dataset.itemCount;
      Cart.setCount(Number(fresh.dataset.itemCount || 0));
      trackTierState(this);
      restoreFocus(this, focusKey);
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* Cart page */
  class CartPage extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      bindCartLines(this, () => this.querySelector('.cart-page__items') || this);
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-cart-note]')) Cart.updateNote(event.target.value);
      });
    }

    renderFrom(html) {
      const doc = parseHTML(html);
      const fresh = doc.querySelector('cart-page');
      if (!fresh) return;
      const focusKey = getFocusKey(this);
      this.innerHTML = fresh.innerHTML;
      restoreFocus(this, focusKey);
    }
  }
  if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);

  /* Shared cart line behaviour (quantity, remove, quantity-offer shortcut) */
  function bindCartLines(root, getBusyTarget) {
    const pending = new Map();

    const update = async (keys, quantity) => {
      const busyTarget = getBusyTarget();
      busyTarget && busyTarget.classList.add('is-updating');
      root.setAttribute('aria-busy', 'true');
      Z.announce(strings.cartUpdating);
      hideError(root);
      try {
        const data = await Cart.updateKeys(keys, quantity);
        Cart.render(data.sections);
        Cart.setCount(data.item_count);
        Z.announce(strings.cartUpdated);
      } catch (error) {
        showError(root, error.description || strings.cartError);
        const fresh = getBusyTarget();
        fresh && fresh.classList.remove('is-updating');
      } finally {
        root.removeAttribute('aria-busy');
        const target = getBusyTarget();
        target && target.classList.remove('is-updating');
      }
    };

    const keysOf = (value) => (value || '').split(',').filter(Boolean);

    root.addEventListener('change', (event) => {
      const input = event.target.closest('.cart-line .qty__input');
      if (!input) return;
      const keys = keysOf(input.dataset.keys);
      if (!keys.length) return;
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      const row = input.dataset.line;
      clearTimeout(pending.get(row));
      pending.set(row, setTimeout(() => update(keys, quantity), 350));
    });

    root.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-keys]');
      if (remove) {
        event.preventDefault();
        update(keysOf(remove.dataset.removeKeys), 0);
        return;
      }
      const setKeys = event.target.closest('[data-cart-set-keys]');
      if (setKeys) {
        event.preventDefault();
        update(keysOf(setKeys.dataset.cartSetKeys), Number(setKeys.dataset.quantity));
      }
    });
  }

  function showError(root, message) {
    const el = root.querySelector('[data-cart-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }
  function hideError(root) {
    const el = root.querySelector('[data-cart-error]');
    if (el) el.hidden = true;
  }

  function getFocusKey(root) {
    const active = document.activeElement;
    if (!active || !root.contains(active)) return null;
    const line = active.closest('[data-line]');
    let role = null;
    if (active.matches('.qty__input')) role = '.qty__input';
    else if (active.matches('[data-qty-plus]')) role = '[data-qty-plus]';
    else if (active.matches('[data-qty-minus]')) role = '[data-qty-minus]';
    else if (active.matches('[data-cart-set-keys]')) role = '[data-cart-set-keys]';
    return { line: line ? line.dataset.line : null, role };
  }

  function restoreFocus(root, key) {
    if (!key) return;
    let target = null;
    if (key.line && key.role) target = root.querySelector(`.cart-line[data-line="${key.line}"] ${key.role}`);
    if (!target && key.role === '[data-cart-set-keys]') target = root.querySelector('.cart-line .qty__input');
    if (!target) target = root.querySelector('[data-drawer-close]') || root.querySelector('.cart-line__title');
    if (target) target.focus({ preventScroll: true });
  }

  /* ---------------------------------------------------------------------- */
  /* Quantity input                                                         */
  /* ---------------------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[data-qty-minus]');
      this.plus = this.querySelector('[data-qty-plus]');
      this.minus && this.minus.addEventListener('click', () => this.step(-1));
      this.plus && this.plus.addEventListener('click', () => this.step(1));
      this.input && this.input.addEventListener('change', () => this.sync());
      this.sync();
    }

    get min() { return Number(this.input.getAttribute('min') || 0); }
    get max() { return this.input.hasAttribute('max') ? Number(this.input.getAttribute('max')) : Infinity; }

    step(delta) {
      if (this.input.disabled) return;
      const current = parseInt(this.input.value, 10) || 0;
      const next = Math.min(this.max, Math.max(this.min, current + delta));
      if (next === current) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    sync() {
      let value = parseInt(this.input.value, 10);
      if (Number.isNaN(value)) value = Math.max(this.min, 1);
      value = Math.min(this.max, Math.max(this.min, value));
      if (String(value) !== this.input.value) this.input.value = value;
      if (this.minus) this.minus.setAttribute('aria-disabled', String(value <= this.min));
      if (this.plus) this.plus.setAttribute('aria-disabled', String(value >= this.max));
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------------------------------------------------------------------- */
  /* Sticky header                                                          */
  /* ---------------------------------------------------------------------- */

  /* The header height is fixed in CSS (--header-h: 60px, 80px from 1080px); the header itself
     is sized from that token, so it must never be measured back into it. */
  class StickyHeader extends HTMLElement {}
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ---------------------------------------------------------------------- */
  /* Video frame: loop that plays only while at least half visible; starts  */
  /* muted (the only way browsers autoplay) and has a sound button          */
  /* ---------------------------------------------------------------------- */

  class ZkVideo extends HTMLElement {
    connectedCallback() {
      this.setup();
      // Re-observe on every connect: the theme editor detaches and re-attaches sections when they move
      if (this.video && 'IntersectionObserver' in window) {
        if (this.observer) this.observer.disconnect();
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          this.update();
        }, { threshold: [0, 0.5, 1] });
        this.observer.observe(this);
      }
    }

    setup() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-video-toggle]');
      this.soundToggle = this.querySelector('[data-video-sound]');
      if (!this.video) return;
      this.video.muted = true;
      this.video.playsInline = true;
      this.video.removeAttribute('autoplay');
      // Under "reduce motion" the video never starts by itself; the control still lets people play it.
      this.userPaused = reduceMotion.matches;
      this.visible = false;

      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.play();
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        });
      }
      if (this.soundToggle) {
        this.soundToggle.hidden = false;
        // Unmuting inside the tap is what lets browsers play the sound; a paused video starts too
        this.soundToggle.addEventListener('click', () => {
          this.video.muted = !this.video.muted;
          if (!this.video.muted && this.video.paused) {
            this.userPaused = false;
            this.play();
          }
        });
      }
      this.video.addEventListener('play', () => this.sync());
      this.video.addEventListener('pause', () => this.sync());
      this.video.addEventListener('volumechange', () => this.sync());
      this.sync();
    }

    update() {
      if (!this.video) return;
      const shouldPlay = this.visible && !this.userPaused && !document.hidden;
      if (shouldPlay && this.video.paused) this.play();
      else if (!shouldPlay && !this.video.paused) this.video.pause();
    }

    play() {
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch((error) => {
          // Sound refused outside a tap (e.g. scrolled back into view): keep the loop going muted
          if (error && error.name === 'NotAllowedError' && !this.video.muted) {
            this.video.muted = true;
            this.play();
          } else {
            this.sync();
          }
        });
      }
    }

    sync() {
      if (this.toggle) {
        const paused = this.video.paused;
        this.toggle.toggleAttribute('data-paused', paused);
        this.toggle.setAttribute('aria-label', paused ? strings.playVideo || '' : strings.pauseVideo || '');
      }
      if (this.soundToggle) {
        const muted = this.video.muted;
        this.soundToggle.toggleAttribute('data-muted', muted);
        this.soundToggle.setAttribute('aria-label', muted ? strings.unmuteVideo || '' : strings.muteVideo || '');
      }
    }

    disconnectedCallback() {
      this.observer && this.observer.disconnect();
    }
  }
  if (!customElements.get('zk-video')) customElements.define('zk-video', ZkVideo);
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('zk-video').forEach((el) => typeof el.update === 'function' && el.update());
  });

  /* ---------------------------------------------------------------------- */
  /* Accordions, analytics hooks, reviews, cookie preferences               */
  /* ---------------------------------------------------------------------- */

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const title = (details.querySelector('.accordion__title') || details.querySelector('summary') || {}).textContent;
    const kind = details.dataset.analytics || (details.closest('[data-analytics-faq]') ? 'faq' : null);
    if (kind === 'faq') Z.publish('zikra_faq_opened', { question: (title || '').trim() });
    if (kind === 'shipping') Z.publish('zikra_shipping_info_viewed', { source: 'accordion' });
  }, true);

  const initShippingView = () => {
    const blocks = document.querySelectorAll('[data-shipping-info]:not([data-observed])');
    if (!blocks.length || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        Z.publish('zikra_shipping_info_viewed', { source: 'product_block' });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.6 });
    blocks.forEach((block) => {
      block.dataset.observed = 'true';
      observer.observe(block);
    });
  };

  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-reviews-more]');
    if (more) {
      const list = more.parentElement.querySelector('[data-reviews-list]');
      const hidden = list ? Array.from(list.querySelectorAll(':scope > li:nth-child(n + 6):not(.is-shown)')) : [];
      hidden.slice(0, 5).forEach((item) => item.classList.add('is-shown'));
      if (hidden.length <= 5) more.hidden = true;
      return;
    }

    const cookieLink = event.target.closest('[data-cookie-preferences]');
    if (cookieLink && window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
      event.preventDefault();
      window.privacyBanner.showPreferences();
    }
  });

  /* ---------------------------------------------------------------------- */
  /* Init + theme editor support                                            */
  /* ---------------------------------------------------------------------- */

  const init = () => {
    initShippingView();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', () => {
    initShippingView();
  });
})();
